import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import {
  ClipboardList, Plus, Search, Save, Loader, X, Check, AlertCircle,
  Building2, Eye, Trash2, Calendar, Package, ArrowLeft,
} from 'lucide-react';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL;

// Guard de tema: mismos valores por defecto que GestionStock (colores de marca
// SIEMPRE inline; nunca clases Tailwind dinámicas, que el purge se las come).
const useSafeTheme = () => {
  const { theme } = useTheme();
  return {
    primary:       theme?.primary       || '#3cbf9a',
    primaryDark:   theme?.primaryDark   || '#2da080',
    primaryLight:  theme?.primaryLight  || 'rgba(60, 191, 154, 0.10)',
    primaryBorder: theme?.primaryBorder || 'rgba(60, 191, 154, 0.20)',
  };
};

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const fmtFecha = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES') : '—');

// Debounce (copiado de NuevoPedido.js) — 300 ms para el buscador de catálogo.
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Normaliza un tipo (mayúsculas, sin acentos) para comparar con tolerancia,
// mismo criterio que plantillaPara() en el backend.
const normTipo = (s) =>
  String(s || '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

// ¿El tipo tiene plantilla? Coincidencia exacta o por prefijo/contiene, igual
// que la resolución tolerante del backend ("AUMENTO DE PECHO SIMPLE" ⇒ sí).
const tipoTienePlantilla = (tipo, tiposPlantilla) => {
  if (!tipo || !Array.isArray(tiposPlantilla)) return false;
  const t = normTipo(tipo);
  if (!t) return false;
  return tiposPlantilla.some((clave) => {
    const k = normTipo(clave);
    return k === t || t.includes(k) || k.includes(t);
  });
};

// Secciones del parte de quirófano. `null`/'' → "Sin clasificar".
const SECCIONES = [
  { key: 'general',      label: 'General' },
  { key: 'infiltracion', label: 'Infiltración' },
  { key: 'suturas',      label: 'Suturas' },
  { key: 'atb',          label: 'ATB' },
];
const seccionLabel = (key) => {
  const s = SECCIONES.find((x) => x.key === key);
  return s ? s.label : 'Sin clasificar';
};

// ─── Toast (réplica del de GestionStock) ──────────────────────────────────────
const Toast = ({ toast, theme }) => (
  <div
    className="fixed bottom-6 right-6 z-50 max-w-sm px-4 py-3 rounded-lg shadow-xl text-white"
    style={{ backgroundColor: toast.type === 'success' ? theme.primary : '#dc2626' }}
  >
    <div className="flex items-start gap-2">
      {toast.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
      <span className="text-sm font-medium">{toast.msg}</span>
    </div>
  </div>
);

// ─── Modal de cabecera (nueva operación / editar) ─────────────────────────────
const OperacionModal = ({ open, initial, theme, saving, tiposPlantilla, onClose, onSave }) => {
  const [codigo, setCodigo] = useState('');
  const [tipoSel, setTipoSel] = useState('');       // valor del select
  const [tipoLibre, setTipoLibre] = useState('');   // texto cuando es "Otro"
  const [fecha, setFecha] = useState('');
  const [notas, setNotas] = useState('');
  const [cargarPlantilla, setCargarPlantilla] = useState(true);

  // Al abrir, precargar (edición) o limpiar (alta).
  useEffect(() => {
    if (!open) return;
    const tipo = initial?.tipo_intervencion || '';
    setCodigo(initial?.codigo || '');
    if (tipo && !tiposPlantilla.includes(tipo)) {
      setTipoSel('__otro__');
      setTipoLibre(tipo);
    } else {
      setTipoSel(tipo);
      setTipoLibre('');
    }
    setFecha(initial?.fecha ? String(initial.fecha).slice(0, 10) : '');
    setNotas(initial?.notas || '');
    setCargarPlantilla(true);
  }, [open, initial, tiposPlantilla]);

  if (!open) return null;

  const esEdicion = Boolean(initial?.id);
  const esOtro = tipoSel === '__otro__';
  const tipoFinal = esOtro ? tipoLibre.trim() : tipoSel;
  const puedeGuardar = codigo.trim().length > 0 && !saving;

  // El checkbox de precarga solo tiene sentido al CREAR y si el tipo elegido
  // tiene plantilla (no es "Otro" ni un tipo sin material estándar).
  const mostrarCheckPlantilla = !esEdicion && !esOtro && tipoTienePlantilla(tipoFinal, tiposPlantilla);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!puedeGuardar) return;
    onSave({
      codigo: codigo.trim(),
      tipo_intervencion: tipoFinal || null,
      fecha: fecha || null,
      notas: notas.trim() || null,
      // Solo se precarga al crear y con el checkbox visible + marcado.
      cargar_plantilla: mostrarCheckPlantilla ? cargarPlantilla : false,
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden"
      >
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: theme.primaryLight }}>
          <h3 className="font-bold text-gray-800 text-base flex items-center gap-2">
            <ClipboardList size={20} style={{ color: theme.primaryDark }} />
            {initial?.id ? 'Editar operación' : 'Nueva operación'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Código <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="Ej. Operación 456"
              autoFocus
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none"
              onFocus={(e) => (e.target.style.borderColor = theme.primary)}
              onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Tipo de intervención</label>
            <select
              value={tipoSel}
              onChange={(e) => setTipoSel(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none bg-white"
            >
              <option value="">— Sin especificar —</option>
              {tiposPlantilla.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
              <option value="__otro__">Otro (escribir)</option>
            </select>
            {esOtro && (
              <input
                type="text"
                value={tipoLibre}
                onChange={(e) => setTipoLibre(e.target.value)}
                placeholder="Escribe el tipo de intervención"
                className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none"
                onFocus={(e) => (e.target.style.borderColor = theme.primary)}
                onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
              />
            )}

            {/* Precarga de material estándar: solo al crear y si el tipo tiene
                plantilla. Marcado por defecto. */}
            {mostrarCheckPlantilla && (
              <label
                className="mt-3 flex items-start gap-2 p-2.5 rounded-lg cursor-pointer select-none"
                style={{ backgroundColor: theme.primaryLight, border: `1px solid ${theme.primaryBorder}` }}
              >
                <input
                  type="checkbox"
                  checked={cargarPlantilla}
                  onChange={(e) => setCargarPlantilla(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300"
                  style={{ accentColor: theme.primary }}
                />
                <span className="text-xs text-gray-700">
                  <strong className="font-semibold" style={{ color: theme.primaryDark }}>
                    Cargar material estándar de esta intervención
                  </strong>
                  <br />
                  Se precargarán las líneas habituales de esta operación. Podrás
                  ajustarlas después.
                </span>
              </label>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Fecha</label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Notas</label>
            <textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              rows={2}
              placeholder="Opcional"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none resize-none"
              onFocus={(e) => (e.target.style.borderColor = theme.primary)}
              onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
            />
          </div>
        </div>

        <div className="px-6 pb-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!puedeGuardar}
            className="px-4 py-2 text-sm font-semibold text-white rounded shadow flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: theme.primary }}
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Modal para añadir / editar una línea de material ─────────────────────────
const MaterialModal = ({ open, initial, clienteId, theme, saving, onClose, onSave }) => {
  const [descripcion, setDescripcion] = useState('');
  const [productoId, setProductoId] = useState(null);
  const [cantidad, setCantidad] = useState('1');
  const [seccion, setSeccion] = useState('general');

  // Sugerencias de catálogo, buscadas sobre el propio texto del material.
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const debouncedDescripcion = useDebounce(descripcion, 300);
  // Texto del producto ya vinculado: si el usuario reescribe y difiere, se
  // desvincula y pasa a texto libre.
  const [vinculadoTexto, setVinculadoTexto] = useState('');

  // Precargar al abrir (edición) o limpiar (alta).
  useEffect(() => {
    if (!open) return;
    setDescripcion(initial?.descripcion || '');
    setProductoId(initial?.producto_id ?? null);
    setVinculadoTexto(initial?.producto_id ? (initial?.descripcion || '') : '');
    setCantidad(initial?.cantidad != null ? String(initial.cantidad) : '1');
    setSeccion(initial?.seccion || 'general');
    setResultados([]);
    setDropdownOpen(false);
  }, [open, initial]);

  // Búsqueda en catálogo con debounce.
  useEffect(() => {
    if (!open) return;
    const q = debouncedDescripcion.trim();
    // No sugerir cuando el texto ya es el del producto vinculado.
    if (q.length < 2 || !clienteId || q === vinculadoTexto) { setResultados([]); return; }
    let cancelado = false;
    setBuscando(true);
    fetch(
      `${API_URL}/api/operaciones/catalogo/buscar?cliente_id=${clienteId}&query=${encodeURIComponent(q)}`,
      { headers: authHeaders() },
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!cancelado) { setResultados(Array.isArray(d) ? d : []); setDropdownOpen(true); } })
      .catch(() => { if (!cancelado) setResultados([]); })
      .finally(() => { if (!cancelado) setBuscando(false); });
    return () => { cancelado = true; };
  }, [debouncedDescripcion, open, clienteId, vinculadoTexto]);

  if (!open) return null;

  const elegirProducto = (p) => {
    setProductoId(p.id);
    setDescripcion(p.nombre);
    setVinculadoTexto(p.nombre);
    setResultados([]);
    setDropdownOpen(false);
  };

  // Escribir en el campo: si había producto vinculado y el texto ya no
  // coincide, se desvincula y pasa a texto libre.
  const onDescripcionChange = (valor) => {
    setDescripcion(valor);
    if (productoId && valor !== vinculadoTexto) {
      setProductoId(null);
      setVinculadoTexto('');
    }
  };

  const cantNum = parseInt(cantidad, 10);
  const puedeGuardar = descripcion.trim().length > 0 && cantNum > 0 && !saving;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!puedeGuardar) return;
    onSave({
      producto_id: productoId,
      descripcion: descripcion.trim(),
      cantidad: cantNum,
      seccion: seccion || null,
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden"
      >
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: theme.primaryLight }}>
          <h3 className="font-bold text-gray-800 text-base flex items-center gap-2">
            <Package size={20} style={{ color: theme.primaryDark }} />
            {initial?.id ? 'Editar material' : 'Añadir material'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Un solo campo: escribes el material. Si coincide con algo del
              catálogo aparecen sugerencias para vincularlo (y traer su precio);
              si no eliges ninguna, se guarda tal cual como texto libre. */}
          <div className="relative">
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Material <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                <Search size={14} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={descripcion}
                onChange={(e) => onDescripcionChange(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.primary;
                  if (resultados.length) setDropdownOpen(true);
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  // Retraso para permitir el click en un resultado.
                  setTimeout(() => setDropdownOpen(false), 150);
                }}
                placeholder={'Escribe el material (p. ej. bisturí, o "aguja amarilla")'}
                className="w-full pr-8 py-2 text-sm border border-gray-300 rounded focus:outline-none"
                style={{ paddingLeft: '32px' }}
              />
              {buscando && (
                <div className="absolute inset-y-0 right-0 pr-2.5 flex items-center">
                  <Loader size={14} className="animate-spin text-gray-400" />
                </div>
              )}
            </div>

            {dropdownOpen && resultados.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                {resultados.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => elegirProducto(p)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0"
                  >
                    <div className="font-medium text-gray-800 truncate">{p.nombre}</div>
                    <div className="text-xs text-gray-500 flex gap-2">
                      {p.ref_proveedor && <span>Ref. {p.ref_proveedor}</span>}
                      {p.unidadbasica && <span>· {p.unidadbasica}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Estado del campo: vinculado al catálogo o texto libre. */}
            {productoId ? (
              <p className="mt-1.5 text-xs font-medium" style={{ color: theme.primaryDark }}>
                ✓ Vinculado al catálogo (se guarda con su precio)
              </p>
            ) : (
              <p className="mt-1.5 text-xs text-gray-500">
                Si no aparece en el catálogo, se guarda tal cual (texto libre).
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Cantidad</label>
              <input
                type="number"
                min="1"
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none"
                onFocus={(e) => (e.target.style.borderColor = theme.primary)}
                onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Sección</label>
              <select
                value={seccion}
                onChange={(e) => setSeccion(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none bg-white"
              >
                {SECCIONES.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
                <option value="">Sin clasificar</option>
              </select>
            </div>
          </div>
        </div>

        <div className="px-6 pb-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!puedeGuardar}
            className="px-4 py-2 text-sm font-semibold text-white rounded shadow flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: theme.primary }}
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar material
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Vista LISTA ──────────────────────────────────────────────────────────────
const ListaView = ({
  theme, isAdmin, clientes, clienteSel, onSelectCliente,
  operaciones, loading, onNueva, onAbrir, onBorrar,
}) => (
  <div className="max-w-6xl mx-auto px-4 py-6">
    <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
      <div>
        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <ClipboardList size={22} style={{ color: theme.primary }} />
          Consumos quirúrgicos
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Registro anónimo de material consumido por operación.
        </p>
      </div>
      <button
        onClick={onNueva}
        disabled={!clienteSel}
        className="px-4 py-2 text-sm font-semibold text-white rounded-lg shadow flex items-center gap-2 disabled:opacity-50"
        style={{ backgroundColor: theme.primary }}
      >
        <Plus size={16} /> Nueva operación
      </button>
    </div>

    {/* Selector de clínica: admin, o cualquier usuario con más de una clínica
        accesible (p.ej. una matriz con hijas). Sin esto, un usuario no-admin
        con 2+ clínicas se quedaba sin selector y sin autoselección. */}
    {(isAdmin || clientes.length > 1) && (
      <div className="mb-5 max-w-md">
        <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
          <Building2 size={13} /> Clínica
        </label>
        <select
          value={clienteSel?.id || ''}
          onChange={(e) => {
            const c = clientes.find((x) => String(x.id) === e.target.value);
            onSelectCliente(c || null);
          }}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none bg-white"
        >
          <option value="">— Selecciona una clínica —</option>
          {clientes.map((c) => (
            <option key={c.id} value={c.id}>{c.razon_social || c.alias || `Clínica ${c.id}`}</option>
          ))}
        </select>
      </div>
    )}

    {!clienteSel ? (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
        <Building2 size={40} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm">{isAdmin || clientes.length > 0 ? 'Selecciona una clínica para ver sus operaciones.' : 'Sin clínica asignada.'}</p>
      </div>
    ) : loading ? (
      <div className="p-12 text-center text-gray-400">
        <Loader size={24} className="animate-spin mx-auto" />
      </div>
    ) : operaciones.length === 0 ? (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
        <ClipboardList size={40} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm">Todavía no hay operaciones registradas.</p>
      </div>
    ) : (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Código</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Tipo de intervención</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Fecha</th>
                <th className="text-center px-4 py-2.5 font-semibold text-gray-700">Nº materiales</th>
                <th className="text-right px-4 py-2.5 font-semibold text-gray-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {operaciones.map((op) => (
                <tr
                  key={op.id}
                  className="border-t hover:bg-gray-50 cursor-pointer"
                  onClick={() => onAbrir(op)}
                >
                  <td className="px-4 py-2.5 font-medium text-gray-800">{op.codigo}</td>
                  <td className="px-4 py-2.5 text-gray-600">{op.tipo_intervencion || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{fmtFecha(op.fecha)}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span
                      className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}
                    >
                      {op.num_lineas ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => onAbrir(op)}
                        title="Ver / editar"
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => onBorrar(op)}
                        title="Borrar"
                        className="p-1.5 rounded hover:bg-red-50 text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

// ─── Vista DETALLE ────────────────────────────────────────────────────────────
const DetalleView = ({
  theme, operacion, lineas, loading, tiposPlantilla, dudas, cargandoPlantilla,
  onVolver, onEditarCabecera, onAñadirMaterial, onCargarPlantilla,
  onEditarLinea, onBorrarLinea,
}) => {
  // Agrupar líneas por sección respetando el orden de SECCIONES y "Sin clasificar" al final.
  const grupos = useMemo(() => {
    const orden = [...SECCIONES.map((s) => s.key), null];
    const map = new Map(orden.map((k) => [k, []]));
    (lineas || []).forEach((l) => {
      const key = l.seccion && SECCIONES.some((s) => s.key === l.seccion) ? l.seccion : null;
      map.get(key).push(l);
    });
    return orden
      .map((key) => ({ key, label: seccionLabel(key), items: map.get(key) }))
      .filter((g) => g.items.length > 0);
  }, [lineas]);

  const total = (lineas || []).length;
  const tienePlantilla = tipoTienePlantilla(operacion?.tipo_intervencion, tiposPlantilla);

  // Notas de cantidades dudosas del PDF que afectan a este tipo (informativo).
  const dudasTipo = useMemo(() => {
    const t = normTipo(operacion?.tipo_intervencion);
    if (!t || !Array.isArray(dudas)) return [];
    return dudas.filter((d) => {
      const cabecera = normTipo(String(d).split('·')[0]);
      return cabecera && (t.includes(cabecera) || cabecera.includes(t));
    });
  }, [dudas, operacion]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <button
        onClick={onVolver}
        className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-4"
      >
        <ArrowLeft size={16} /> Volver
      </button>

      {/* Cabecera */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{operacion?.codigo}</h1>
            <div className="mt-1 flex items-center gap-3 flex-wrap text-sm text-gray-500">
              <span>{operacion?.tipo_intervencion || 'Sin tipo'}</span>
              <span className="flex items-center gap-1">
                <Calendar size={13} /> {fmtFecha(operacion?.fecha)}
              </span>
            </div>
            {operacion?.notas && (
              <p className="mt-2 text-sm text-gray-600 whitespace-pre-line">{operacion.notas}</p>
            )}
          </div>
          <button
            onClick={onEditarCabecera}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            Editar
          </button>
        </div>
      </div>

      {/* Materiales */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold text-gray-800 flex items-center gap-2">
          <Package size={18} style={{ color: theme.primary }} /> Materiales
          <span className="text-sm font-normal text-gray-400">({total})</span>
        </h2>
        <div className="flex items-center gap-2">
          {tienePlantilla && (
            <button
              onClick={onCargarPlantilla}
              disabled={cargandoPlantilla}
              title="Cargar el material estándar de este tipo de intervención"
              className="px-3 py-1.5 text-sm font-semibold rounded-lg border flex items-center gap-2 disabled:opacity-50 bg-white"
              style={{ color: theme.primaryDark, borderColor: theme.primaryBorder }}
            >
              {cargandoPlantilla
                ? <Loader size={15} className="animate-spin" />
                : <ClipboardList size={15} />}
              Cargar material estándar
            </button>
          )}
          <button
            onClick={onAñadirMaterial}
            className="px-3 py-1.5 text-sm font-semibold text-white rounded-lg shadow flex items-center gap-2"
            style={{ backgroundColor: theme.primary }}
          >
            <Plus size={15} /> Añadir material
          </button>
        </div>
      </div>

      {/* Resumen por sección */}
      {grupos.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {grupos.map((g) => (
            <span
              key={g.key ?? 'null'}
              className="text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 bg-gray-50"
            >
              {g.label}: <strong>{g.items.length}</strong>
            </span>
          ))}
        </div>
      )}

      {/* Aviso de cantidades dudosas del parte para este tipo (informativo). */}
      {dudasTipo.length > 0 && (
        <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50">
          <div className="flex items-start gap-2">
            <AlertCircle size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-800">
              <strong className="font-semibold">Cantidades a confirmar con quirófano:</strong>
              <ul className="mt-1 list-disc list-inside space-y-0.5">
                {dudasTipo.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-12 text-center text-gray-400">
          <Loader size={24} className="animate-spin mx-auto" />
        </div>
      ) : total === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400">
          <Package size={36} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm">Sin materiales registrados. Añade el primero.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {grupos.map((g) => (
            <div key={g.key ?? 'null'} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b text-xs font-semibold text-gray-600 uppercase tracking-wide">
                {g.label}
              </div>
              <ul>
                {g.items.map((l) => (
                  <li key={l.id} className="px-4 py-2.5 border-t first:border-t-0 flex items-center gap-3 hover:bg-gray-50">
                    <span
                      className="flex-shrink-0 inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded text-sm font-bold"
                      style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}
                    >
                      {l.cantidad}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-gray-800">{l.descripcion}</span>
                      {l.producto_id && l.producto_nombre && (
                        <span className="text-xs text-gray-400 ml-2">({l.producto_nombre})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => onEditarLinea(l)}
                        title="Editar"
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                      >
                        <ClipboardList size={15} />
                      </button>
                      <button
                        onClick={() => onBorrarLinea(l)}
                        title="Borrar"
                        className="p-1.5 rounded hover:bg-red-50 text-red-500"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Página principal ─────────────────────────────────────────────────────────
export default function OperacionesConsumo() {
  const { user } = useContext(AuthContext);
  const theme = useSafeTheme();
  const isAdmin = user?.permisos === 1;
  const isClient = user?.permisos === 5;

  const [vista, setVista] = useState('lista'); // 'lista' | 'detalle'
  const [toast, setToast] = useState(null);
  const notify = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 5000);
  }, []);

  // Tipos con plantilla de material estándar + notas de cantidades dudosas.
  const [tiposPlantilla, setTiposPlantilla] = useState([]);
  const [dudas, setDudas] = useState([]);

  // Clientes disponibles + selección
  const [clientes, setClientes] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);

  // Lista de operaciones
  const [operaciones, setOperaciones] = useState([]);
  const [loadingLista, setLoadingLista] = useState(false);

  // Detalle
  const [operacionId, setOperacionId] = useState(null);
  const [operacion, setOperacion] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  // Modales
  const [opModal, setOpModal] = useState({ open: false, initial: null });
  const [matModal, setMatModal] = useState({ open: false, initial: null });
  const [savingOp, setSavingOp] = useState(false);
  const [savingMat, setSavingMat] = useState(false);
  const [cargandoPlantilla, setCargandoPlantilla] = useState(false);

  // Carga de los tipos que tienen plantilla de material estándar (para el select
  // de tipo y el botón / checkbox de precarga). Endpoint: { tipos, dudas }.
  useEffect(() => {
    fetch(`${API_URL}/api/operaciones/plantillas`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { tipos: [], dudas: [] }))
      .then((d) => {
        setTiposPlantilla(Array.isArray(d?.tipos) ? d.tipos : []);
        setDudas(Array.isArray(d?.dudas) ? d.dudas : []);
      })
      .catch(() => { setTiposPlantilla([]); setDudas([]); });
  }, []);

  // Carga inicial de clientes. Endpoint propio de operaciones: a diferencia del
  // de stock, NO filtra por gestion_stock, así una matriz ve todas sus hijas
  // aunque no gestionen stock (registrar consumos no depende de eso).
  useEffect(() => {
    fetch(`${API_URL}/api/operaciones/clientes`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { clientes: [] }))
      .then((d) => {
        const list = d.clientes || [];
        setClientes(list);
        // Cliente (o admin con una sola clínica): autoseleccionar.
        if (list.length === 1) setClienteSel(list[0]);
      })
      .catch(() => setClientes([]));
  }, []);

  // Cargar operaciones del cliente seleccionado.
  const cargarOperaciones = useCallback(() => {
    if (!clienteSel) { setOperaciones([]); return; }
    setLoadingLista(true);
    fetch(`${API_URL}/api/operaciones?cliente_id=${clienteSel.id}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      // El backend responde { operaciones: [...] }. Aceptamos tambien un array
      // pelado por si el contrato cambia, pero la clave real es d.operaciones:
      // sin esto la lista salia siempre vacia aunque hubiera operaciones.
      .then((d) => setOperaciones(Array.isArray(d?.operaciones) ? d.operaciones : (Array.isArray(d) ? d : [])))
      .catch(() => setOperaciones([]))
      .finally(() => setLoadingLista(false));
  }, [clienteSel]);

  useEffect(() => { cargarOperaciones(); }, [cargarOperaciones]);

  // Cargar detalle de una operación.
  const cargarDetalle = useCallback((id) => {
    if (!id) return;
    setLoadingDetalle(true);
    fetch(`${API_URL}/api/operaciones/${id}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        setOperacion(d?.operacion || null);
        setLineas(d?.lineas || []);
      })
      .catch(() => { setOperacion(null); setLineas([]); })
      .finally(() => setLoadingDetalle(false));
  }, []);

  useEffect(() => {
    if (vista === 'detalle' && operacionId) cargarDetalle(operacionId);
  }, [vista, operacionId, cargarDetalle]);

  // Abrir detalle desde la lista.
  const abrirDetalle = useCallback((op) => {
    setOperacionId(op.id);
    setOperacion(op);
    setLineas([]);
    setVista('detalle');
  }, []);

  const volverLista = useCallback(() => {
    setVista('lista');
    setOperacionId(null);
    setOperacion(null);
    setLineas([]);
    cargarOperaciones();
  }, [cargarOperaciones]);

  // Guardar cabecera (alta o edición).
  const guardarOperacion = useCallback(async (payload) => {
    if (!clienteSel) return;
    setSavingOp(true);
    try {
      const editar = opModal.initial?.id;
      const url = editar
        ? `${API_URL}/api/operaciones/${opModal.initial.id}`
        : `${API_URL}/api/operaciones`;
      const r = await fetch(url, {
        method: editar ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ cliente_id: clienteSel.id, ...payload }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || 'Error al guardar la operación');
      setOpModal({ open: false, initial: null });
      notify('success', editar ? 'Operación actualizada' : 'Operación creada');
      if (editar) {
        cargarDetalle(opModal.initial.id);
      } else {
        // Entrar al detalle de la recién creada.
        const nuevaId = data.id ?? data.operacion?.id;
        if (nuevaId) {
          setOperacionId(nuevaId);
          setOperacion(null);
          setLineas([]);
          setVista('detalle');
        } else {
          cargarOperaciones();
        }
      }
    } catch (e) {
      notify('error', e.message);
    } finally {
      setSavingOp(false);
    }
  }, [clienteSel, opModal, notify, cargarDetalle, cargarOperaciones]);

  // Borrar operación (con confirmación).
  const borrarOperacion = useCallback(async (op) => {
    if (!window.confirm(`¿Borrar la operación "${op.codigo}"? Se eliminarán también sus materiales.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/operaciones/${op.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!r.ok) throw new Error('No se pudo borrar la operación');
      notify('success', 'Operación borrada');
      cargarOperaciones();
    } catch (e) {
      notify('error', e.message);
    }
  }, [notify, cargarOperaciones]);

  // Guardar línea de material (alta o edición).
  const guardarMaterial = useCallback(async (payload) => {
    if (!operacionId) return;
    setSavingMat(true);
    try {
      const editar = matModal.initial?.id;
      const url = editar
        ? `${API_URL}/api/operaciones/${operacionId}/lineas/${matModal.initial.id}`
        : `${API_URL}/api/operaciones/${operacionId}/lineas`;
      const r = await fetch(url, {
        method: editar ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || 'Error al guardar el material');
      setMatModal({ open: false, initial: null });
      notify('success', editar ? 'Material actualizado' : 'Material añadido');
      cargarDetalle(operacionId);
    } catch (e) {
      notify('error', e.message);
    } finally {
      setSavingMat(false);
    }
  }, [operacionId, matModal, notify, cargarDetalle]);

  // Cargar la plantilla de material estándar en la operación abierta. Si ya hay
  // materiales, preguntar si se AÑADEN (no reemplazamos sin avisar). Si está
  // vacía, cargar directamente.
  const cargarPlantillaDetalle = useCallback(async () => {
    if (!operacionId) return;
    const tieneMateriales = (lineas || []).length > 0;
    if (tieneMateriales) {
      const ok = window.confirm(
        'Esta operación ya tiene materiales. ¿Añadir la plantilla estándar a lo que ya hay?\n\n' +
        'Aceptar = añadir la plantilla. Cancelar = no hacer nada.'
      );
      if (!ok) return;
    }
    setCargandoPlantilla(true);
    try {
      const r = await fetch(`${API_URL}/api/operaciones/${operacionId}/cargar-plantilla`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ reemplazar: false }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || 'No se pudo cargar la plantilla');
      notify('success', `Plantilla cargada: ${data.insertadas ?? 0} materiales añadidos`);
      cargarDetalle(operacionId);
    } catch (e) {
      notify('error', e.message);
    } finally {
      setCargandoPlantilla(false);
    }
  }, [operacionId, lineas, notify, cargarDetalle]);

  // Borrar línea de material.
  const borrarMaterial = useCallback(async (linea) => {
    if (!operacionId) return;
    if (!window.confirm(`¿Borrar "${linea.descripcion}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/operaciones/${operacionId}/lineas/${linea.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!r.ok) throw new Error('No se pudo borrar el material');
      notify('success', 'Material borrado');
      cargarDetalle(operacionId);
    } catch (e) {
      notify('error', e.message);
    }
  }, [operacionId, notify, cargarDetalle]);

  // Acceso restringido.
  if (!isAdmin && !isClient) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-2xl shadow p-10 text-center max-w-md">
          <ClipboardList size={40} className="mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Acceso restringido</h2>
          <p className="text-gray-600 text-sm">Esta sección es solo para administradores y clínicas.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
      {vista === 'lista' ? (
        <ListaView
          theme={theme}
          isAdmin={isAdmin}
          clientes={clientes}
          clienteSel={clienteSel}
          onSelectCliente={setClienteSel}
          operaciones={operaciones}
          loading={loadingLista}
          onNueva={() => setOpModal({ open: true, initial: null })}
          onAbrir={abrirDetalle}
          onBorrar={borrarOperacion}
        />
      ) : (
        <DetalleView
          theme={theme}
          operacion={operacion}
          lineas={lineas}
          loading={loadingDetalle}
          tiposPlantilla={tiposPlantilla}
          dudas={dudas}
          cargandoPlantilla={cargandoPlantilla}
          onVolver={volverLista}
          onEditarCabecera={() => setOpModal({ open: true, initial: operacion })}
          onAñadirMaterial={() => setMatModal({ open: true, initial: null })}
          onCargarPlantilla={cargarPlantillaDetalle}
          onEditarLinea={(l) => setMatModal({ open: true, initial: l })}
          onBorrarLinea={borrarMaterial}
        />
      )}

      <OperacionModal
        open={opModal.open}
        initial={opModal.initial}
        theme={theme}
        saving={savingOp}
        tiposPlantilla={tiposPlantilla}
        onClose={() => setOpModal({ open: false, initial: null })}
        onSave={guardarOperacion}
      />

      <MaterialModal
        open={matModal.open}
        initial={matModal.initial}
        clienteId={clienteSel?.id}
        theme={theme}
        saving={savingMat}
        onClose={() => setMatModal({ open: false, initial: null })}
        onSave={guardarMaterial}
      />

      {toast && <Toast toast={toast} theme={theme} />}
    </div>
  );
}
